import { t } from "i18next";
import { useCallback, useState } from "react";

import { getValidationMessage } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { deleteInvoice } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";

type Props = {
	invoiceData: Invoice;
	onCancel: () => void;
	onSuccess: () => void;
};

export const DeleteInvoiceForm = ({
	invoiceData,
	onCancel,
	onSuccess,
}: Props) => {
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const isReversalInvoice = invoiceData.invoiceTypeId === invoiceTypeId.ReversalInvoice;

	const onSubmit = useCallback(async () => {
		try {
			setIsBusy(true);
			await deleteInvoice(invoiceData.id);
			onSuccess();
		} catch (error) {
			console.log(error);
			// A 422 happens when the invoice data in the view is old.
			setServerErrorMessage(getValidationMessage(error, ["invoiceId"]) ?? t("form:unexpectedError"));
		} finally {
			setIsBusy(false);
		}
	}, [invoiceData.id, onSuccess]);

	return (
		<div className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnung löschen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Löschen fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<Hint>
					<p>Eine Rechnung sollte nur in absoluten Ausnahmefällen gelöscht werden und keinesfalls, falls sie
						buchhalterisch bereits verarbeitet wurde oder ein Zahlungseingang erfolgt ist.</p>
					<br />
					<p>Die <strong>Rechnungsnummer</strong> wird bei der Löschung freigegeben und bei
						der Erstellung der nächsten
						Rechnung wiederverwendet.</p>
					<br />
					{isReversalInvoice && <><p>Da es sich bei dieser Rechnung um
						eine <strong>Stornorechnungen</strong> handelt, wird beim Löschen der Status des storinierten
						Rechnung auf &quot;Bezahlt&quot; zurückgesetzt.</p><br />
					</>}
					<p>Alle Kommentare werden unwideruflich gelöscht!</p>

				</Hint>
				{showConfirmation &&
					<div className="mt-12">Bist Du sicher? Dieser Vorgang <strong>kann nicht rückgängig gemacht
						werden!</strong></div>}
			</SidebarContent>
			<SidebarFooter>
				{!showConfirmation && <Button onClick={() => setShowConfirmation(true)}>
					Rechnung löschen
				</Button>}
				{showConfirmation && <Button onClick={onSubmit}>
					Löschung bestätigen
				</Button>}
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</div>);
};

